import { evaluateCondition } from "../pageLogic/evaluatePageLogic";
import type { FormAnswerMap, PageLogicCondition } from "../pageLogic/types";

/** Inclusion and exclusion criteria, in the page-logic condition shape. */
export interface ScreenerCriteria {
    include: PageLogicCondition[];
    exclude: PageLogicCondition[];
}

export type ScreenerOutcome = "eligible" | "ineligible";

function isCondition(value: unknown): value is PageLogicCondition {
    if (!value || typeof value !== "object") return false;
    const condition = value as Record<string, unknown>;
    return typeof condition.input_id === "string" && typeof condition.operator === "string" && "value" in condition;
}

/** Reads `screeners.criteria` jsonb, dropping anything that is not a condition. */
export function parseScreenerCriteria(raw: unknown): ScreenerCriteria {
    const source = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
    const list = (value: unknown) => (Array.isArray(value) ? value.filter(isCondition) : []);
    return { include: list(source.include), exclude: list(source.exclude) };
}

/**
 * Eligible when every inclusion holds and no exclusion does.
 * An unanswered inclusion (a form that ended early) fails, so the patient is ineligible.
 */
export function evaluateScreener(criteria: ScreenerCriteria, answers: FormAnswerMap): ScreenerOutcome {
    const included = criteria.include.every((condition) => evaluateCondition(condition, answers));
    const excluded = criteria.exclude.some(
        (condition) => answers[condition.input_id] != null && evaluateCondition(condition, answers)
    );
    return included && !excluded ? "eligible" : "ineligible";
}
